import React from 'react';
import css from '@styled-system/css';
import styled from 'styled-components';
import { compose, display, maxWidth as maxWidthSystem, space, variant } from 'styled-system'; // Imports maxWidth prop without entire layout system

import { fontWeight, textAlign, textColor } from '@rover/kibble/styles';

import roverTheme from '../../themes/roverTheme';
import Box from '../Box';

import { ParagraphCommonProps } from './Paragraph.common';

export type Props = ParagraphCommonProps;

const StyledParagraph = styled(Box)<Props>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
  },
  (props) => {
    const paragraph = props.theme?.paragraph ? props.theme.paragraph : roverTheme.paragraph;
    return variant({
      prop: 'size',
      variants: paragraph,
    })(props);
  },
  compose(textAlign, textColor, fontWeight, space, display, maxWidthSystem), // Apply last to avoid being overwritten by resets and variant styles
  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme)
);

function Paragraph({
  size = '200',
  a11yLabel,
  role,
  webProps,
  testID,
  ...other
}: Props): JSX.Element {
  const {
    maxWidth = '640px', // reasoning behind this default is at http://www.rover.com/storybook/?path=/docs/kibble-components-paragraph--default-story#length
    as = 'p',
    ...otherWebProps
  } = webProps || {};

  return (
    <StyledParagraph
      role={role}
      aria-label={a11yLabel}
      {...other}
      {...otherWebProps}
      as={as}
      maxWidth={maxWidth}
      size={size}
      data-testid={testID || otherWebProps?.['data-testid'] || other['data-testid']}
    />
  );
}

/**
 * Paragraph is a text component optimized for body copy and longer text content.
 * Includes default max-width for readability and appropriate line-height for reading.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5211586852/Kibble+Core+Paragraph
 */
export default Paragraph;
